/**
 * Analyst Workspace — /analyst/reviews
 *
 * Analyst = intermediate review between submission and admin clearance.
 * Flow: Submitted → Analyst Review → Recommend → Approver → FI Cleared
 *
 * Analyst CAN: view, annotate, risk-score, recommend
 * Analyst CANNOT: final-clear for FI (only Approver/Admin)
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import {
  Shield, RefreshCw, ChevronDown, ChevronRight, CheckCircle2,
  AlertTriangle, MessageSquare, TrendingDown, DollarSign,
  Info, ArrowRight, BarChart2, Clock
} from "lucide-react";

const VERDICT_COLOR = {
  cleared:         "bg-emerald-900/30 text-emerald-400 border-emerald-500/30",
  review_required: "bg-amber-900/30 text-amber-400 border-amber-500/30",
  flagged:         "bg-red-900/30 text-red-400 border-red-500/30",
};
const THRESHOLD_BANDS = [
  { min: 80, max: 100, label: "80–100",  color: "bg-emerald-500", verdict: "Cleared"          },
  { min: 60, max: 79,  label: "60–79",   color: "bg-amber-500",   verdict: "Review Required"  },
  { min: 0,  max: 59,  label: "0–59",    color: "bg-red-500",     verdict: "Flagged"           },
];
const REC_COLOR = {
  proceed:           "text-emerald-400 bg-emerald-900/20 border-emerald-500/30",
  hold:              "text-amber-400 bg-amber-900/20 border-amber-500/30",
  request_revision:  "text-blue-400 bg-blue-900/20 border-blue-500/30",
};

// ── Risk Dimension Bars ────────────────────────────────────────────────────
function RiskDimension({ name, dim }) {
  const pct = Math.round((dim.score / dim.max) * 100);
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-400 capitalize">{name.replace(/_/g," ")}</span>
        <span className="text-white font-bold">{dim.score}/{dim.max}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      {dim.why && <p className="text-[9px] text-slate-600 leading-snug">{dim.why}</p>}
    </div>
  );
}

// ── Deal Review Card ───────────────────────────────────────────────────────
function DealCard({ deal, onReviewed }) {
  const [expanded,  setExpanded]  = useState(false);
  const [form,      setForm]      = useState({
    annotation:     deal.existing_review?.annotation     || "",
    recommendation: deal.existing_review?.recommendation || "",
    risk_notes:     deal.existing_review?.risk_notes     || "",
  });
  const [saving, setSaving] = useState(false);
  const risk = deal.risk_score || {};

  const handleSave = async (rec = null) => {
    setSaving(true);
    try {
      await client.post(`/analyst/reviews/${deal.request_id}`, {
        ...form,
        recommendation: rec || form.recommendation || null,
      });
      toast.success(`Review saved: ${rec || form.recommendation || "no recommendation"}`);
      if (onReviewed) onReviewed();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };

  const TYPE_BADGE = { sme: "bg-teal-900/30 text-teal-400", project: "bg-purple-900/30 text-purple-400", corporate: "bg-blue-900/30 text-blue-400" };

  return (
    <div className="bg-[#0B1220] border border-white/8 rounded-xl overflow-hidden"
      data-testid={`analyst-deal-${deal.request_id}`}>
      {/* Header */}
      <div className="flex items-start gap-4 px-5 py-3 cursor-pointer hover:bg-white/2"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="text-xs font-bold text-white">{deal.org_name}</p>
            <span className="text-[9px] font-mono text-slate-500">{deal.request_id}</span>
            {deal.workspace_type && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${TYPE_BADGE[deal.workspace_type] || "bg-white/10 text-white"}`}>{deal.workspace_type.toUpperCase()}</span>}
            {/* Risk verdict badge */}
            {risk.verdict && (
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${VERDICT_COLOR[risk.verdict] || "bg-white/5 text-white border-white/10"}`}>
                {risk.threshold_label || risk.verdict} · {risk.total}/100
              </span>
            )}
            {/* Existing recommendation */}
            {deal.existing_review?.recommendation && (
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border capitalize ${REC_COLOR[deal.existing_review.recommendation] || "text-slate-400 bg-white/5 border-white/10"}`}>
                Analyst: {deal.existing_review.recommendation.replace(/_/g," ")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            {deal.org_sector && <span>{deal.org_sector}</span>}
            {deal.total_tco2e > 0 && <span>{deal.total_tco2e}t CO₂e</span>}
            {deal.amount > 0 && <span>${(deal.amount/1000).toFixed(0)}k</span>}
            <span className="capitalize">{deal.status?.replace(/_/g," ")}</span>
            {deal.submitted_at && <span>{new Date(deal.submitted_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}
          </div>
        </div>
        {expanded ? <ChevronDown size={13} className="text-slate-500 flex-shrink-0" /> : <ChevronRight size={13} className="text-slate-500 flex-shrink-0" />}
      </div>

      {/* Expanded: Risk + Review Form */}
      {expanded && (
        <div className="border-t border-white/5 px-5 py-4 space-y-5">
          {/* Risk Score breakdown */}
          {risk.dimensions && (
            <div className="bg-[#0F172A] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide font-bold">Risk Assessment</p>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${VERDICT_COLOR[risk.verdict] || "text-slate-400 border-white/10"}`}>
                    {risk.total}/100 — {risk.threshold_label}
                  </span>
                </div>
              </div>
              {/* Threshold guide */}
              <div className="flex items-center gap-2 mb-3 text-[9px]">
                {THRESHOLD_BANDS.map(b => (
                  <div key={b.label} className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${b.color}`} />
                    <span className="text-slate-500">{b.label} = {b.verdict}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(risk.dimensions).map(([k, d]) => <RiskDimension key={k} name={k} dim={d} />)}
              </div>
              {risk.flags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {risk.flags.map(f => (
                    <span key={f} className="text-[8px] font-bold text-amber-400 bg-amber-900/20 px-1.5 py-0.5 rounded">⚠ {f.replace(/_/g," ")}</span>
                  ))}
                </div>
              )}
              {risk.summary && <p className="text-[10px] text-slate-500 mt-2 italic">{risk.summary}</p>}
            </div>
          )}

          {/* Analyst review form */}
          <div className="space-y-3">
            <p className="text-[10px] text-teal-400 uppercase tracking-widest font-bold">Analyst Review</p>

            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-wide mb-1">Analysis Notes</label>
              <textarea value={form.annotation} onChange={e => setForm(f => ({...f, annotation: e.target.value}))}
                placeholder="Describe your analysis of this deal — emissions data quality, financial credibility, pathway suitability, and any specific observations..."
                rows={3}
                className="w-full px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-xs text-white placeholder-slate-600 resize-none focus:outline-none focus:border-teal-500"
                data-testid={`annotation-${deal.request_id}`} />
            </div>

            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-wide mb-1">Risk Notes (optional)</label>
              <textarea value={form.risk_notes} onChange={e => setForm(f => ({...f, risk_notes: e.target.value}))}
                placeholder="Specific risk observations: data gaps, assumption weaknesses, financial outliers, MRV concerns..."
                rows={2}
                className="w-full px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-xs text-white placeholder-slate-600 resize-none focus:outline-none focus:border-teal-500"
                data-testid={`risk-notes-${deal.request_id}`} />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] text-slate-500 mr-1">Recommend:</p>
              <button onClick={() => handleSave("proceed")} disabled={saving}
                className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40"
                data-testid={`recommend-proceed-${deal.request_id}`}>
                {saving ? <RefreshCw size={10} className="animate-spin" /> : "Proceed ✓"}
              </button>
              <button onClick={() => handleSave("hold")} disabled={saving}
                className="px-3 py-1.5 text-[10px] font-bold border border-amber-500/40 text-amber-400 rounded-lg hover:bg-amber-500/10 disabled:opacity-40"
                data-testid={`recommend-hold-${deal.request_id}`}>
                Hold
              </button>
              <button onClick={() => handleSave("request_revision")} disabled={saving}
                className="px-3 py-1.5 text-[10px] font-bold border border-blue-500/40 text-blue-400 rounded-lg hover:bg-blue-500/10 disabled:opacity-40">
                Request Revision
              </button>
              <button onClick={() => handleSave()} disabled={saving}
                className="px-3 py-1.5 text-[10px] font-semibold border border-white/15 text-slate-400 rounded-lg hover:bg-white/5 disabled:opacity-40 ml-auto">
                Save Draft
              </button>
            </div>

            <p className="text-[9px] text-slate-600 italic">
              Analyst recommendations are forwarded to the Approver for final clearance.
              Analyst actions are logged in the platform audit trail.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN ANALYST WORKSPACE ────────────────────────────────────────────────
export default function AnalystWorkspace() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const [queue,     setQueue]   = useState([]);
  const [analyst,   setAnalyst] = useState(null);
  const [loading,   setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/analyst/queue");
      setQueue(res.data.queue || []);
      setAnalyst({ email: res.data.analyst, role: res.data.analyst_role });
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Analyst access required. Contact your platform administrator.");
        navigate("/dashboard");
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const pending    = queue.filter(d => !d.existing_review?.recommendation);
  const reviewed   = queue.filter(d =>  d.existing_review?.recommendation);

  return (
    <div className="min-h-screen bg-[#060D18] text-white" data-testid="analyst-workspace">
      {/* Header */}
      <div className="bg-[#080E1A] border-b border-white/8 px-8 py-5 flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[9px] text-teal-400 uppercase tracking-widest font-bold mb-0.5">DeCarbonIQ</p>
          <h1 className="text-lg font-bold">Analyst Review Workspace</h1>
          {analyst && (
            <p className="text-[10px] text-slate-500 mt-0.5">
              {analyst.email} · {analyst.role?.replace(/_/g," ")} ·
              {" "}Analyst decisions are recorded in the platform audit trail
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-4 text-[11px] text-slate-400">
            <span className="text-amber-400 font-bold">{pending.length} pending</span>
            <span className="text-emerald-400 font-bold">{reviewed.length} reviewed</span>
          </div>
          <button onClick={load} className="text-[11px] text-teal-400 flex items-center gap-1 hover:underline">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      <div className="px-8 py-7 space-y-7">
        {/* Protocol reminder */}
        <div className="bg-[#0B1220] border border-white/8 rounded-xl px-5 py-3 flex items-start gap-3">
          <Info size={13} className="text-teal-400 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-slate-400 leading-relaxed">
            <strong className="text-white">Analyst protocol:</strong> Review each deal against the 4-dimension risk model.
            Recommend Proceed / Hold / Request Revision. Your recommendation goes to the Approver for final clearance.
            You cannot directly clear deals for FI visibility — only Approvers can.
          </p>
        </div>

        {/* Pending queue */}
        {loading ? (
          <div className="text-center py-12"><RefreshCw size={16} className="animate-spin text-teal-500 mx-auto" /></div>
        ) : (
          <>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} className="text-amber-400" />
                <h2 className="text-sm font-bold text-white">Pending Review ({pending.length})</h2>
              </div>
              {pending.length === 0 ? (
                <div className="bg-[#0B1220] border border-white/8 rounded-xl p-6 text-center text-xs text-slate-500">
                  <CheckCircle2 size={20} className="text-teal-500 mx-auto mb-2" />
                  Queue clear — no pending deals.
                </div>
              ) : (
                <div className="space-y-2">
                  {pending.map(d => <DealCard key={d.request_id} deal={d} onReviewed={load} />)}
                </div>
              )}
            </div>

            {reviewed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={14} className="text-emerald-400" />
                  <h2 className="text-sm font-bold text-white">Already Reviewed ({reviewed.length})</h2>
                </div>
                <div className="space-y-2 opacity-70">
                  {reviewed.map(d => <DealCard key={d.request_id} deal={d} onReviewed={load} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
